import { useState } from 'react';
import { api } from '../../api/client.js';
import { Alert, Field, Notice, PageHeader, fehlertext, useLoad } from '../shared/ui.js';

interface Settings {
  minimumOrderValue: string;
  smallOrderSurcharge: string;
  defaultDomesticPackagingFreightAmount: string | null;
  quoteValidityDays: number;
  updatedAt: string;
}

/** Globale kaufmaennische Einstellungen. Nur Betreiber. Keine neuen Parameter. */
export function SettingsPage(): React.JSX.Element {
  const { data, error, reload } = useLoad<Settings>('/api/admin/settings');
  const [f, setF] = useState<Settings | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const w = f ?? data;
  if (w === null) return <Alert text={error} />;

  async function speichern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    setOk(false);
    if (
      !window.confirm(
        'Einstellungen aendern? Sie wirken auf alle kuenftigen Preisvorschauen und Angebote. Bereits ausgestellte Angebote bleiben unveraendert.',
      )
    )
      return;
    try {
      await api.patch('/api/admin/settings', {
        minimumOrderValue: w!.minimumOrderValue.replace(',', '.'),
        smallOrderSurcharge: w!.smallOrderSurcharge.replace(',', '.'),
        defaultDomesticPackagingFreightAmount:
          w!.defaultDomesticPackagingFreightAmount === null ||
          w!.defaultDomesticPackagingFreightAmount === ''
            ? null
            : w!.defaultDomesticPackagingFreightAmount.replace(',', '.'),
        quoteValidityDays: Number(w!.quoteValidityDays),
      });
      setF(null);
      reload();
      setOk(true);
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  const set = (k: keyof Settings, v: string) =>
    setF({
      ...w!,
      [k]:
        k === 'quoteValidityDays'
          ? Number(v)
          : v === '' && k === 'defaultDomesticPackagingFreightAmount'
            ? null
            : v,
    });

  return (
    <>
      <PageHeader
        title="Globale Einstellungen"
        lead="Kaufmaennische Parameter fuer alle Konten. Aenderungen werden protokolliert."
      />
      <form className="card" onSubmit={speichern}>
        <div className="grid2">
          <Field
            label="Mindestauftragswert netto (€)"
            hint="Darunter faellt ein Mindermengenzuschlag an. Bei exakt diesem Wert keiner."
          >
            <input
              value={w.minimumOrderValue}
              onChange={(e) => set('minimumOrderValue', e.target.value)}
            />
          </Field>
          <Field
            label="Maximaler Mindermengenzuschlag (€)"
            hint="Erhoben wird die Differenz bis zum Mindestauftragswert, hoechstens dieser Betrag."
          >
            <input
              value={w.smallOrderSurcharge}
              onChange={(e) => set('smallOrderSurcharge', e.target.value)}
            />
          </Field>
          <Field
            label="Verpackung und Fracht Inland (€)"
            hint="Leer: nicht hinterlegt. Dann muss der Betrag am Angebot angegeben werden; es wird kein Nullwert angenommen."
          >
            <input
              value={w.defaultDomesticPackagingFreightAmount ?? ''}
              placeholder="nicht hinterlegt"
              onChange={(e) =>
                set('defaultDomesticPackagingFreightAmount', e.target.value)
              }
            />
          </Field>
          <Field label="Gueltigkeit eines Angebots (Tage)">
            <input
              type="number"
              min={1}
              max={365}
              value={w.quoteValidityDays}
              onChange={(e) => set('quoteValidityDays', e.target.value)}
            />
          </Field>
        </div>
        <Notice>
          Historische Angebote tragen die bei ihrer Ausstellung verwendeten Werte fest am
          Beleg. Eine Aenderung hier erreicht sie nicht.
        </Notice>
        <Alert text={err} />
        {ok && <p className="pill pill-ok">Gespeichert.</p>}
        <div className="row">
          <button type="submit" className="primary">
            Speichern
          </button>
          <span className="note">
            Zuletzt geaendert: {new Date(w.updatedAt).toLocaleString('de-DE')}
          </span>
        </div>
      </form>
    </>
  );
}
